import { defineStore } from 'pinia';
import { useAxios } from '@/features/shared/axios';
import { ProfileWhitelistRels } from './rels';
import { DefaultPaging, Link, ListResource } from '@/features/shared/models';
import { useCommonStore } from '@/features/shared/commonStore';
import { getLink, hasLink } from '@/features/shared/LinkHelper';
import { NonWhitelistedProfileStats, ProfileWhitelist } from './profileWhitelistModels';
import { Profile } from '@/features/profiles/profilesModels';
import _ from 'lodash';

const EmptyProfileWhitelist: ProfileWhitelist = { id: '', phone: { countryCode: '', number: '' }, phoneCountryCode: '' };

export const useProfileWhitelistStore = defineStore('profile-whitelist', {
    state: () => ({
        profileWhitelists: new Array<ProfileWhitelist>(),
        currentProfileWhitelist: { ...EmptyProfileWhitelist },
        currentProfileWhitelistLinks: new Array<Link>(),
        nonWhitelistedProfilesPaging: { ...DefaultPaging },
        nonWhitelistedProfilesLinks: new Array<Link>(),
        links: new Array<Link>(),
        nonWhitelistedStats: new Array<NonWhitelistedProfileStats>(),
        nonWhitelistedProfiles: new Array<Profile>(),
        nonWhitelistedQuery: { phoneCountryCode: '' }
    }),
    getters: {
        getProfileWhitelistById: state => {
            return (id: string) => state.profileWhitelists.find(item => item.id === id) as ProfileWhitelist;
        },
        isValidProfileWhitelist: state => {
            return !!state.currentProfileWhitelist.phone?.number || !!state.currentProfileWhitelist.phoneCountryCode;
        },
        hasDeleteNonWhitelistedProfilesLink: state => {
            return hasLink(ProfileWhitelistRels.DeleteNonWhitelistedProfiles, state.nonWhitelistedProfilesLinks);
        }
    },
    actions: {
        async loadProfileWhitelists() {
            const common = useCommonStore();
            const axios = useAxios();
            const link = common.getLink(ProfileWhitelistRels.GetProfileWhitelists);

            if (!link) throw new Error(`${ProfileWhitelistRels.GetProfileWhitelists} link not found`);

            const response = await axios.requestByLink<ListResource<ProfileWhitelist>>(link);
            this.profileWhitelists = response.data.data;
            this.links = response.data.links;
        },
        async loadProfileWhitelist(id: string) {
            let profileWhitelist = this.getProfileWhitelistById(id);
            if (!profileWhitelist) {
                await this.loadProfileWhitelists();
                profileWhitelist = this.getProfileWhitelistById(id);
            }

            await this.loadCurrentProfileWhitelist(profileWhitelist);
        },
        async createProfileWhitelistByPhone() {
            const common = useCommonStore();
            const axios = useAxios();
            const link = common.getLink(ProfileWhitelistRels.CreateProfileWhitelistByPhone);

            if (!link) throw new Error(`${ProfileWhitelistRels.CreateProfileWhitelistByPhone} link not found`);

            const profileWhitelistData = { ...this.currentProfileWhitelist };
            await axios.requestByLink(link, { data: profileWhitelistData });
        },
        async createProfileWhitelistByCountryCode() {
            const common = useCommonStore();
            const axios = useAxios();
            const link = common.getLink(ProfileWhitelistRels.CreateProfileWhitelistByCountryCode);

            if (!link) throw new Error(`${ProfileWhitelistRels.CreateProfileWhitelistByCountryCode} link not found`);

            const profileWhitelistData = { phoneCountryCode: this.currentProfileWhitelist.phoneCountryCode };
            await axios.requestByLink(link, { data: profileWhitelistData });
        },
        async loadCurrentProfileWhitelist(profileWhitelist: any) {
            const axios = useAxios();
            const link = getLink(ProfileWhitelistRels.GetProfileWhitelist, profileWhitelist.links);
            const response = await axios.requestByLink(link);

            this.currentProfileWhitelist = response.data.data;
            this.currentProfileWhitelistLinks = response.data.links;
        },
        clearCurrentProfileWhitelist() {
            this.currentProfileWhitelist = _.cloneDeep(EmptyProfileWhitelist);
            this.currentProfileWhitelistLinks = new Array<Link>();
        },
        async deleteProfileWhitelist(id: string) {
            await this.loadProfileWhitelist(id);

            const axios = useAxios();
            const link = getLink(ProfileWhitelistRels.DeleteProfileWhitelist, this.currentProfileWhitelistLinks);

            if (!link) throw new Error(`${ProfileWhitelistRels.DeleteProfileWhitelist} link not found`);

            await axios.requestByLink(link);
        },
        async loadNonWhitelistedStats() {
            const common = useCommonStore();
            const axios = useAxios();
            const link = common.getLink(ProfileWhitelistRels.GetNonWhitelistedProfileStats);

            if (!link) throw new Error(`${ProfileWhitelistRels.GetNonWhitelistedProfileStats} link not found`);

            const response = await axios.requestByLink<NonWhitelistedProfileStats[]>(link);
            this.nonWhitelistedStats = response.data ?? new Array<NonWhitelistedProfileStats>();
        },
        async loadNonWhitelistedProfiles() {
            const common = useCommonStore();
            const axios = useAxios();
            const link = common.getLink(ProfileWhitelistRels.FindNonWhitelistedProfiles);

            if (!link) throw new Error(`${ProfileWhitelistRels.FindNonWhitelistedProfiles} link not found`);

            if (!this.nonWhitelistedQuery.phoneCountryCode) {
                throw new Error('Phone country code must be set in nonWhitelistedQuery');
            }

            const requestParams = new URLSearchParams();
            requestParams.append('page.number', this.nonWhitelistedProfilesPaging.pageNumber.toString());
            requestParams.append('page.size', this.nonWhitelistedProfilesPaging.pageSize.toString());
            requestParams.append('phoneCountryCode', this.nonWhitelistedQuery.phoneCountryCode);

            const response = await axios.requestByLink<ListResource<Profile>>(link, {
                params: requestParams
            });

            this.nonWhitelistedProfiles = response.data.data ?? new Array<Profile>();
            this.nonWhitelistedProfilesPaging = response.data.paging;
            this.nonWhitelistedProfilesLinks = response.data.links;
        },
        async deleteNonWhitelistedProfiles() {
            const axios = useAxios();
            const link = getLink(ProfileWhitelistRels.DeleteNonWhitelistedProfiles, this.nonWhitelistedProfilesLinks);

            if (!link) throw new Error(`${ProfileWhitelistRels.DeleteNonWhitelistedProfiles} link not found`);

            await axios.requestByLink(link);
        }
    }
});
